import { useEffect, useRef, useState } from "react";

import ArrowDownIcon from "../../../assets/arrow-down.svg?react";
import "./SectorDescriptionGroup.css";
import { hasSectors } from "../../../utils/lighthouseUtils";
import { getMapView, highlightFeature } from "../../../utils/arcgisUtils";
import SectorColorIndicator from "../../MapTools/LightHouse/SectorColorIndicator";
import type FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import type Graphic from "@arcgis/core/Graphic";
import type { ResourceHandle } from "@arcgis/core/core/Handles";

interface SectorDescriptionGroupProps {
    feature: Graphic | null | undefined;
}

const SectorDescriptionGroup = (props: SectorDescriptionGroupProps) => {
    const { feature } = props;

    const [open, setOpen] = useState(false);
    const [sectors, setSectors] = useState<{ sectorData: Graphic; sectorGraphic: Graphic | null }[]>([]);

    const hightlightHandleRef = useRef<ResourceHandle[]>([]);

    const hightlightSector = async (sector: Graphic | null) => {
        const view = getMapView();
        if (!view) return;
        if (sector === null) return;

        if (!sector.layer) return;
        for (const handle of hightlightHandleRef.current) {
            handle.remove();
        }
        hightlightHandleRef.current = [];
        const highlightHandle = await highlightFeature(sector, undefined, sector.layer as FeatureLayer);
        if (!highlightHandle) return;
        hightlightHandleRef.current.push(highlightHandle);
    };

    const loadSectorData = async () => {
        if (!feature) {
            setSectors([]);
            return;
        }

        if (!(await hasSectors(feature))) {
            setSectors([]);
            return;
        }
        const view = getMapView();
        if (!view?.map) {
            setSectors([]);
            return;
        }
        const sectorTable = view.map.findTableById("Lyssektor") as FeatureLayer;
        const sectorLayer = view.map.findLayerById("Sektorflate") as FeatureLayer;
        if (!sectorTable || !sectorLayer) {
            setSectors([]);
            return;
        }

        const results = await sectorTable.queryFeatures({
            where: "fknavinst = '" + feature.attributes.globalid + "'",
            outFields: ["*"],
            returnGeometry: false,
        });

        if (!results) {
            setSectors([]);
            return;
        }

        const orderedResults = results.features.toSorted((a, b) => {
            const sectorNumberA = a.attributes["sektor_nr"] || 0;
            const sectorNumberB = b.attributes["sektor_nr"] || 0;
            return sectorNumberA - sectorNumberB;
        });

        const sectorResults = await sectorLayer.queryFeatures({
            where: "fksektorid IN (" + orderedResults.map((s) => `'${s.attributes["globalid"]}'`).join(",") + ")",
            outFields: ["*"],
            returnGeometry: true,
            geometryPrecision: 20,
        });
        const sectorGraphics = sectorResults.features;
        const sectorData = orderedResults.map((sector) => {
            const sectorGraphic = sectorGraphics.find((s) => s.attributes.fksektorid === sector.attributes["globalid"]);
            return { sectorData: sector, sectorGraphic: sectorGraphic || null };
        });
        setSectors(sectorData);
    };

    useEffect(() => {
        loadSectorData();
    }, [feature]);

    useEffect(() => {
        return () => {
            for (const handle of hightlightHandleRef.current) {
                handle.remove();
            }
            hightlightHandleRef.current = [];
        };
    }, []);

    if (sectors?.length === 0) {
        return null;
    }

    return (
        <>
            <button className="sector-description-group" onClick={() => setOpen(!open)}>
                <span className="sector-description-group-title">Sektorer {"(" + sectors?.length + ")"}</span>
                <ArrowDownIcon style={{ rotate: open ? "180deg" : "" }} />
            </button>
            {open && (
                <div>
                    <div className="sector-description-group-list">
                        {sectors?.map((sector) => (
                            <div
                                key={sector.sectorData.attributes.globalid}
                                onMouseEnter={() => hightlightSector(sector.sectorGraphic)}
                                className="sector-description-group-list-item"
                            >
                                <span className="flex-row w-100 space-between">
                                    <span>Sektor nr. {sector.sectorData.attributes["sektor_nr"]}</span>
                                    <SectorColorIndicator
                                        color={sector.sectorData.attributes["lys_farge"]?.toString()}
                                        height="24px"
                                    />
                                </span>
                                <span className="flex-row w-100 center">
                                    <span className="flex-row w-100">
                                        {sector.sectorData.attributes["beskrivelse"]}
                                    </span>
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </>
    );
};

export default SectorDescriptionGroup;
